import { Alert, LinearProgress, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import React, { useState } from 'react';
import { FormattedMessage, IntlShape, MessageDescriptor, useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import {
    getActivities,
    getActivityPdf,
    getActivityStatuses,
    getActivityTypes,
    getInterventionPeriodTypes,
    getWatchlistActivities,
} from '../../api/activities-nim';
import {
    ActivityGridReadDTO,
    DocumentType,
    FeatureFlag,
    ListOfValuesReadDTO,
    PersonReadDTO,
    SavedFilterType,
} from '../../api/dto';
import { getFacilities } from '../../api/values';
import { useApplicationContext } from '../../components/application/ApplicationContextProvider';
import { ImpactLink } from '../../components/ImpactLink';
import { PersonTooltip } from '../../components/PersonTooltip';
import { DocumentResultCard } from '../../components/searching/DocumentResultCard';
import {
    BooleanFilterOption,
    FilterOperatorEnum,
    FilterTypeEnum,
    SearchFilter,
} from '../../components/searching/filters';
import { SearchPageProps } from '../../components/searching/SearchPage';
import { SearchPageWithPdfPreview } from '../../components/searching/SearchPageWithPdfPreview';
import { StatusChip } from '../../components/StatusChip';
import { DATATYPES } from '../../components/tables/table/utils';
import { accessPointFilterSchema } from '../../forms/schemas/accessPoints';
import { contractsFilterSchema } from '../../forms/schemas/contracts';
import { interventionPeriodFilterSchema } from '../../forms/schemas/interventionPeriod';
import { locationFilterSchema } from '../../forms/schemas/location';
import { orgUnitFilterSchema } from '../../forms/schemas/orgUnit';
import { personFilterSchema } from '../../forms/schemas/person';
import { systemFilterSchema } from '../../forms/schemas/system';
import { tagFilterSchema } from '../../forms/schemas/tag';
import { valueFilterSchema } from '../../forms/schemas/value';
import { TFilterElement } from '../../forms/types';
import { fetchActivityProposedInterventionPeriods, fetchActivityScheduledInterventionPeriods } from '../../lib/api';
import { INACTIVE_ACTIVITY_STATUSES } from '../../lib/filters/activity';
import { EntityType } from '../../lib/information/types/EntityType';
import { getLovLabel } from '../../lib/label';
import { translateValue } from '../../lib/language';
import { formatVersionedId } from '../../lib/records/documents/versioned';
import { getActivityStatus } from '../../lib/status';
import { getRoute, ROUTES } from '../../routes/routes';

export const filterSchemas = (intl: IntlShape, features?: Partial<Record<FeatureFlag, boolean>>): TFilterElement[] => [
    {
        id: 'id',
        label: intl.formatMessage({ id: 'activity.id' }),
        type: FilterTypeEnum.NUMBER,
    },
    valueFilterSchema(
        {
            id: 'status.code',
            label: intl.formatMessage({ id: 'document.field.status' }),
            fetchOptions: getActivityStatuses,
        },
        intl.locale
    ),
    orgUnitFilterSchema({
        id: 'activityResponsible.orgUnit.code',
        label: intl.formatMessage({ id: 'document.field.responsibleGroup' }),
    }),
    valueFilterSchema(
        {
            id: 'facility.code',
            label: intl.formatMessage({ id: 'document.field.facility' }),
            fetchOptions: getFacilities,
        },
        intl.locale
    ),
    valueFilterSchema(
        {
            id: 'type.code',
            label: intl.formatMessage({ id: 'document.field.type' }),
            fetchOptions: getActivityTypes,
        },
        intl.locale
    ),
    tagFilterSchema({
        id: 'activityTags.tag.id',
        label: intl.formatMessage({ id: 'activity.field.tags' }),
    }),
    systemFilterSchema({
        id: 'system.id',
        label: intl.formatMessage({ id: 'activity.field.system' }),
    }),
    {
        id: 'title',
        label: intl.formatMessage({ id: 'document.field.title' }),
        type: FilterTypeEnum.STRING,
    },
    {
        id: 'description',
        label: intl.formatMessage({ id: 'document.field.description' }),
        type: FilterTypeEnum.STRING,
    },
    interventionPeriodFilterSchema({
        id: 'proposedInterventionPeriod.periodName',
        label: intl.formatMessage({ id: 'activity.field.proposedInterventionPeriod' }),
        fetchEndpoint: fetchActivityProposedInterventionPeriods,
        watchPaths: ['filters'],
    }),
    interventionPeriodFilterSchema({
        id: 'interventionPeriod.periodName',
        label: intl.formatMessage({ id: 'activity.field.scheduledInterventionPeriod' }),
        fetchEndpoint: fetchActivityScheduledInterventionPeriods,
        watchPaths: ['filters'],
    }),
    {
        id: 'proposedDate',
        label: intl.formatMessage({ id: 'activity.field.proposedDate' }),
        type: FilterTypeEnum.DATE,
    },
    {
        id: 'scheduledStart',
        label: intl.formatMessage({ id: 'activity.field.scheduledStart' }),
        type: FilterTypeEnum.DATE,
    },
    {
        id: 'scheduledEnd',
        label: intl.formatMessage({ id: 'activity.field.scheduledEnd' }),
        type: FilterTypeEnum.DATE,
    },
    {
        id: 'accessStart',
        label: intl.formatMessage({ id: 'activity.field.accessStart' }),
        type: FilterTypeEnum.DATE,
    },
    {
        id: 'accessEnd',
        label: intl.formatMessage({ id: 'activity.field.accessEnd' }),
        type: FilterTypeEnum.DATE,
    },
    {
        id: 'activityPeopleWithActiveTasks.deadlineDate',
        label: intl.formatMessage({ id: 'task.field.deadline' }),
        type: FilterTypeEnum.DATE,
    },
    personFilterSchema({ id: 'createdBy.searchLabel', label: intl.formatMessage({ id: 'document.field.creator' }) }),
    personFilterSchema({
        id: 'activityResponsible.searchLabel',
        label: intl.formatMessage({ id: 'document.field.responsible' }),
    }),
    personFilterSchema({
        id: 'activityParticipantsExpanded.person.searchLabel',
        label: intl.formatMessage({ id: 'activity.field.participant' }),
    }),
    personFilterSchema({
        id: 'activityParticipantsWithoutAccess.person.searchLabel',
        label: intl.formatMessage({ id: 'activity.field.participantWithoutAccessYet' }),
    }),
    personFilterSchema({
        id: 'activityPeopleWithActiveTasks.person.searchLabel',
        label: intl.formatMessage({ id: 'activity.field.activityPersonWithActiveTasks' }),
    }),
    locationFilterSchema({
        id: 'activityLocations.location.id',
        label: intl.formatMessage({ id: 'document.field.location' }),
    }),
    valueFilterSchema(
        {
            id: 'proposedInterventionPeriod.periodType.code',
            label: intl.formatMessage({ id: 'activity.field.proposedInterventionPeriodType' }),
            fetchOptions: getInterventionPeriodTypes,
        },
        intl.locale
    ),
    valueFilterSchema(
        {
            id: 'interventionPeriod.periodType.code',
            label: intl.formatMessage({ id: 'activity.field.scheduledInterventionPeriodType' }),
            fetchOptions: getInterventionPeriodTypes,
        },
        intl.locale
    ),
    accessPointFilterSchema({
        id: 'activityLocations.accessPoints.name',
        label: intl.formatMessage({ id: 'document.location.info.accessPoints' }),
    }),
    contractsFilterSchema({
        id: 'activityParticipants.companyContract.id',
        label: intl.formatMessage({ id: 'activity.field.contract.contract' }),
    }),
    ...(features?.[FeatureFlag.PREASSESSED_TEMPLATE]
        ? [
              {
                  id: 'isTemplate',
                  label: intl.formatMessage({ id: 'search.filters.activityTemplates' }),
                  type: FilterTypeEnum.BOOLEAN,
              },
          ]
        : []),
];

const getIAmParticipantFilter = (person: PersonReadDTO | undefined): SearchFilter[] => [
    {
        field: 'activityParticipantsExpanded.person.searchLabel',
        operator: FilterOperatorEnum.IS,
        value: person,
    },
];

const getIAmParticipantActiveFilter = (
    person: PersonReadDTO | undefined,
    statuses: ListOfValuesReadDTO[]
): SearchFilter[] => [
    {
        field: 'activityParticipantsExpanded.person.searchLabel',
        operator: FilterOperatorEnum.IS,
        value: person,
    },
    {
        field: 'status.code',
        operator: FilterOperatorEnum.IS_ANY_OF,
        value: statuses.filter((s) => !INACTIVE_ACTIVITY_STATUSES.has(s.code)),
    },
];

const getIAmParticipantWithoutAccessFilter = (person: PersonReadDTO | undefined): SearchFilter[] => [
    {
        field: 'activityParticipantsWithoutAccess.person.searchLabel',
        operator: FilterOperatorEnum.IS,
        value: person,
    },
];

const getMyUpcomingSignaturesFilter = (person: PersonReadDTO | undefined): SearchFilter[] => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Without this the predefined filter does not stay selected
    const twoWeeksFromNow = new Date(today);
    twoWeeksFromNow.setDate(today.getDate() + 14);

    return [
        {
            field: 'activityPeopleWithActiveTasks.person.searchLabel',
            operator: FilterOperatorEnum.IS,
            value: person,
        },
        {
            field: 'activityPeopleWithActiveTasks.deadlineDate',
            operator: FilterOperatorEnum.ON_OR_AFTER,
            value: today,
        },
        {
            field: 'activityPeopleWithActiveTasks.deadlineDate',
            operator: FilterOperatorEnum.ON_OR_BEFORE,
            value: twoWeeksFromNow,
        },
    ];
};

const getTemplatesFilter = (person: PersonReadDTO, intl: IntlShape): SearchFilter[] => [
    {
        field: 'isTemplate',
        operator: FilterOperatorEnum.IS,
        value: {
            id: BooleanFilterOption.TRUE,
            label: intl.formatMessage({ id: 'search.filters.value.true' }),
        },
    },
    {
        field: 'activityParticipantsExpanded.person.searchLabel',
        operator: FilterOperatorEnum.IS,
        value: person,
    },
];

const columnHelper = createColumnHelper<ActivityGridReadDTO>();

const getColumns = (intl: IntlShape) => [
    columnHelper.accessor((row) => row.id ?? '', {
        id: 'id',
        header: intl.formatMessage({ id: 'activity.name' }),
        size: 60,
        cell: ({ getValue, row: { original } }) => (
            <ImpactLink
                to={getRoute({
                    path: ROUTES.activity.view,
                    params: { id: (original as any).id },
                })}
            >
                {getValue()}
            </ImpactLink>
        ),
    }),
    columnHelper.accessor((row) => getLovLabel(translateValue(row.status, intl.locale)) ?? '', {
        id: 'status.label',
        header: intl.formatMessage({ id: 'document.field.status' }),
        size: 120,
        cell: ({ row }) => <StatusChip {...getActivityStatus(row.original, intl)} size="small" />,
        meta: {
            dataType: DATATYPES.ENUMERATION,
        },
    }),
    columnHelper.accessor((row) => row.activityResponsible?.orgUnit?.code ?? '', {
        id: 'activityResponsible.orgUnit.code',
        header: intl.formatMessage({ id: 'document.field.responsibleGroup' }),
        size: 135,
    }),
    columnHelper.accessor(
        (row) => (row.facility?.description ? getLovLabel(translateValue(row.facility, intl.locale)) : ''),
        {
            id: 'facility.code',
            header: intl.formatMessage({ id: 'document.field.facility' }),
            size: 120,
            meta: {
                dataType: DATATYPES.ENUMERATION,
            },
        }
    ),
    columnHelper.accessor((row) => row.interventionPeriod?.periodName ?? '', {
        id: 'interventionPeriod.periodName',
        header: intl.formatMessage({ id: 'activity.field.scheduledInterventionPeriod' }),
        size: 180,
    }),
    columnHelper.accessor(
        (row) =>
            row.interventionPeriod?.periodType
                ? getLovLabel(translateValue(row.interventionPeriod.periodType, intl.locale))
                : '',
        {
            id: 'interventionPeriod.periodType.label',
            header: intl.formatMessage({ id: 'activity.field.scheduledInterventionPeriodType' }),
            size: 180,
            meta: {
                dataType: DATATYPES.ENUMERATION,
                initiallyHidden: true,
            },
        }
    ),
    columnHelper.accessor((row) => row.proposedInterventionPeriod?.periodName ?? '', {
        id: 'proposedInterventionPeriod.periodName',
        header: intl.formatMessage({ id: 'activity.field.proposedInterventionPeriod' }),
        size: 180,
    }),
    columnHelper.accessor(
        (row) =>
            row.proposedInterventionPeriod?.periodType
                ? getLovLabel(translateValue(row.proposedInterventionPeriod.periodType, intl.locale))
                : '',
        {
            id: 'proposedInterventionPeriod.periodType.label',
            header: intl.formatMessage({ id: 'activity.field.proposedInterventionPeriodType' }),
            size: 180,
            meta: {
                dataType: DATATYPES.ENUMERATION,
                initiallyHidden: true,
            },
        }
    ),
    columnHelper.accessor((row) => (row.type ? getLovLabel(translateValue(row.type, intl.locale)) : ''), {
        id: 'type.code',
        header: intl.formatMessage({ id: 'document.field.type' }),
        size: 120,
    }),
    columnHelper.accessor((row) => row.system?.name ?? '', {
        id: 'system.name',
        header: intl.formatMessage({ id: 'activity.field.system' }),
        size: 100,
    }),
    columnHelper.accessor((row) => row.title ?? '', {
        id: 'title',
        header: intl.formatMessage({ id: 'document.field.title' }),
        size: 260,
    }),
    columnHelper.accessor((row) => row.maxNbParticipants ?? '', {
        id: 'maxNbParticipants',
        header: intl.formatMessage({ id: 'activity.field.maxNumberOfParticipants' }),
        size: 190,
    }),
    columnHelper.accessor((row) => row.description ?? '', {
        id: 'description',
        header: intl.formatMessage({ id: 'document.field.description' }),
        size: 300,
    }),
    columnHelper.accessor((row) => row.proposedDate ?? '', {
        id: 'proposedDate',
        header: intl.formatMessage({ id: 'activity.field.proposedDate' }),
        size: 100,
        meta: {
            dataType: DATATYPES.DATE,
            initiallyHidden: true,
        },
    }),
    columnHelper.accessor((row) => row.duration ?? '', {
        id: 'duration',
        header: intl.formatMessage({ id: 'activity.field.duration' }),
        size: 100,
        enableSorting: false,
        cell: ({ row: { original }, getValue }) =>
            !!original.duration &&
            !!original.durationType && (
                <Typography variant="inherit">
                    {`${getValue()} ${
                        intl.locale === 'fr' ? original.durationType.labelFr : original.durationType.labelEn
                    }`}
                </Typography>
            ),
        meta: {
            initiallyHidden: true,
        },
    }),
    columnHelper.accessor((row) => row.workingTimeAvail, {
        id: 'workingTimeAvail.code',
        header: intl.formatMessage({ id: 'activity.field.workingTimeAvail' }),
        size: 100,
        cell: ({ row: { original }, getValue }) =>
            !!original.workingTimeAvail && (
                <Typography variant="inherit">
                    {intl.locale === 'fr' ? getValue()?.labelFr : getValue()?.labelEn}
                </Typography>
            ),
        meta: {
            initiallyHidden: true,
        },
    }),
    columnHelper.accessor((row) => row.scheduledStart ?? '', {
        id: 'scheduledStart',
        header: intl.formatMessage({ id: 'activity.field.scheduledStart' }),
        size: 100,
        meta: {
            dataType: DATATYPES.DATE,
            initiallyHidden: true,
        },
    }),
    columnHelper.accessor((row) => row.scheduledEnd ?? '', {
        id: 'scheduledEnd',
        header: intl.formatMessage({ id: 'activity.field.scheduledEnd' }),
        size: 100,
        meta: {
            dataType: DATATYPES.DATE,
            initiallyHidden: true,
        },
    }),
    columnHelper.accessor((row) => row.accessStart ?? '', {
        id: 'accessStart',
        header: intl.formatMessage({ id: 'activity.field.accessStart' }),
        size: 100,
        meta: {
            dataType: DATATYPES.DATE,
            initiallyHidden: true,
        },
    }),
    columnHelper.accessor((row) => row.accessEnd ?? '', {
        id: 'accessEnd',
        header: intl.formatMessage({ id: 'activity.field.accessEnd' }),
        size: 100,
        meta: {
            dataType: DATATYPES.DATE,
            initiallyHidden: true,
        },
    }),
    columnHelper.accessor((row) => row.workingTimeGtd, {
        id: 'workingTimeGtd.code',
        header: intl.formatMessage({ id: 'activity.field.workingTimeGtd' }),
        size: 100,
        cell: ({ row: { original }, getValue }) =>
            !!original.workingTimeGtd && (
                <Typography variant="inherit">
                    {intl.locale === 'fr' ? getValue()?.labelFr : getValue()?.labelEn}
                </Typography>
            ),
        meta: {
            initiallyHidden: true,
        },
    }),
    columnHelper.accessor((row) => row.is37s, {
        id: 'is37s',
        header: intl.formatMessage({ id: 'is37.name' }),
        size: 250,
        enableSorting: false,
        cell: ({ row: { original }, getValue }) =>
            !!original.is37s && (
                <Typography variant="inherit">
                    {getValue().map((is37, index) => (
                        <React.Fragment key={is37.id}>
                            {index > 0 && ', '}
                            <ImpactLink
                                to={getRoute({
                                    path: ROUTES.is37.view,
                                    params: { id: is37.id },
                                })}
                            >
                                {is37.id}
                            </ImpactLink>
                        </React.Fragment>
                    ))}
                </Typography>
            ),
        meta: {
            initiallyHidden: true,
        },
    }),
    columnHelper.accessor((row) => row.firePermits, {
        id: 'firePermits',
        header: intl.formatMessage({ id: 'firePermit.name' }),
        size: 250,
        enableSorting: false,
        cell: ({ row: { original }, getValue }) =>
            !!original.firePermits && (
                <Typography variant="inherit">
                    {getValue().map((firePermit, index) => (
                        <React.Fragment key={firePermit.id}>
                            {index > 0 && ', '}
                            <ImpactLink
                                to={getRoute({
                                    path: ROUTES.firePermit.view,
                                    params: { id: firePermit.id },
                                })}
                            >
                                {firePermit.id}
                            </ImpactLink>
                        </React.Fragment>
                    ))}
                </Typography>
            ),
        meta: {
            initiallyHidden: true,
        },
    }),
    columnHelper.accessor((row) => row.rpAssessment, {
        id: 'dimr',
        header: intl.formatMessage({ id: 'dimr.name' }),
        size: 250,
        enableSorting: false,
        cell: ({ getValue }) => {
            const dimrVersion = getValue()?.dimrVersion;
            return dimrVersion ? (
                <Typography variant="inherit">
                    <ImpactLink
                        to={getRoute({
                            path: ROUTES.dimr.view,
                            params: {
                                id: dimrVersion.dimrMaster.id,
                                version: dimrVersion.versionNumber,
                            },
                        })}
                    >
                        {formatVersionedId(dimrVersion.dimrMaster.id, dimrVersion.versionNumber)}
                    </ImpactLink>
                </Typography>
            ) : null;
        },
        meta: {
            initiallyHidden: true,
        },
    }),
    columnHelper.accessor((row) => row.activityLocations, {
        id: 'locations',
        header: intl.formatMessage({ id: 'document.field.locations' }),
        size: 250,
        enableSorting: false,
        cell: ({ row: { original }, getValue }) =>
            !!original.activityLocations && (
                <Typography variant="inherit">
                    {getValue()
                        .map((location) => location.location?.impactName)
                        .join(', ')}
                </Typography>
            ),
        meta: {
            initiallyHidden: true,
        },
    }),
    columnHelper.accessor((row) => row.lastModifiedBy?.searchLabel ?? '', {
        id: 'lastModifiedBy.searchLabel',
        header: intl.formatMessage({ id: 'activity.field.lastModifiedBy' }),
        size: 200,
        cell: ({ row: { original }, getValue }) =>
            !!original.lastModifiedBy && (
                <PersonTooltip person={original.lastModifiedBy}>
                    <Typography variant="inherit">{getValue()}</Typography>
                </PersonTooltip>
            ),
        meta: {
            initiallyHidden: true,
        },
    }),
    columnHelper.accessor((row) => row.lastModifiedDate ?? '', {
        id: 'lastModifiedDate',
        header: intl.formatMessage({ id: 'activity.field.lastModifiedDate' }),
        size: 100,
        meta: {
            dataType: DATATYPES.DATE,
            initiallyHidden: true,
        },
    }),
    columnHelper.accessor((row) => row.createdBy?.searchLabel ?? '', {
        id: 'createdBy.searchLabel',
        header: intl.formatMessage({ id: 'document.field.creator' }),
        size: 200,
        cell: ({ row: { original }, getValue }) =>
            !!original.createdBy && (
                <PersonTooltip person={original.createdBy}>
                    <Typography variant="inherit">{getValue()}</Typography>
                </PersonTooltip>
            ),
        meta: {
            initiallyHidden: true,
        },
    }),
    columnHelper.accessor((row) => row.activityResponsible?.searchLabel, {
        id: 'activityResponsible.searchLabel',
        header: intl.formatMessage({ id: 'document.field.responsible' }),
        size: 200,
        cell: ({ row: { original }, getValue }) =>
            !!original.activityResponsible && (
                <PersonTooltip person={original.activityResponsible}>
                    <Typography variant="inherit">{getValue()}</Typography>
                </PersonTooltip>
            ),
        meta: {
            initiallyHidden: true,
        },
    }),
    columnHelper.accessor((row) => row.activityResponsible?.phone1, {
        id: 'phone',
        header: `${intl.formatMessage({ id: 'document.person.phone' })} / ${intl.formatMessage({
            id: 'document.person.mobile',
        })}`,
        size: 200,
        enableSorting: false, // Not sure about that one
        cell: ({ row: { original }, getValue }) =>
            !!original.activityResponsible?.phone1 && !!original.activityResponsible?.phone3Display ? (
                <Typography variant="inherit">
                    {getValue()} / {original.activityResponsible.phone3Display}
                </Typography>
            ) : (
                <Typography variant="inherit">{getValue()}</Typography>
            ),
        meta: {
            initiallyHidden: true,
        },
    }),
    columnHelper.accessor((row) => row.activityTags, {
        id: 'tags',
        header: intl.formatMessage({ id: 'activity.field.tags' }),
        size: 80,
        enableSorting: false,
        cell: ({ row: { original }, getValue }) =>
            !!original.activityTags && (
                <Typography variant="inherit">
                    {getValue()
                        .map((activityTag) => activityTag.tag.name)
                        .join(', ')}
                </Typography>
            ),
    }),
    //TODO approval type, approval status, approved date, guides and comments after importing them from OIM
];

interface ActivitySearchPageProps {
    isWatchlist?: boolean;
}

export enum ActivityBulkActions {
    INTERRUPT = 'interrupt',
    RESUME = 'resume',
    CLOSE = 'close',
    TAG = 'tag',
}

const ACTIVITY_BULK_ACTIONS: Record<ActivityBulkActions, MessageDescriptor['id']> = {
    [ActivityBulkActions.INTERRUPT]: 'document.action.interrupt',
    [ActivityBulkActions.RESUME]: 'document.action.resume',
    [ActivityBulkActions.CLOSE]: 'document.action.close',
    [ActivityBulkActions.TAG]: 'document.action.tag',
};

export const ActivitySearchPage: React.FC<ActivitySearchPageProps> = ({ isWatchlist = false }) => {
    const intl = useIntl();
    const { currentUser, features } = useApplicationContext();

    const location = useLocation();
    const selectedIds = (location.state?.selectedIds ?? []) as number[];
    const [selectedRows, setSelectedRows] = useState<ActivityGridReadDTO[]>(() =>
        selectedIds.map((id) => ({ id } as ActivityGridReadDTO))
    );

    const {
        isError: isStatusesError,
        isLoading: isLoadingStatuses,
        data: activityStatuses,
    } = useQuery(
        ['activityStatuses'],
        () => {
            return getActivityStatuses();
        },
        { cacheTime: Number.MAX_SAFE_INTEGER }
    );

    if (isLoadingStatuses) {
        return <LinearProgress />;
    } else if (isStatusesError) {
        return (
            <Alert severity="error" sx={{ m: 2 }}>
                <FormattedMessage id="common.error" />
            </Alert>
        );
    }

    const predefinedFilters: NonNullable<SearchPageProps<ActivityGridReadDTO>['predefinedFilters']> = [
        {
            filters: getMyUpcomingSignaturesFilter(currentUser),
            labelKey: 'search.filters.myUpcomingSignatures',
            tooltipKey: 'search.filters.myUpcomingSignatures.tooltip',
        },
        {
            filters: getIAmParticipantFilter(currentUser),
            labelKey: 'search.filters.iAmParticipant',
        },
        {
            filters: getIAmParticipantActiveFilter(currentUser, activityStatuses),
            labelKey: 'search.filters.iAmParticipantActive',
        },
        {
            filters: getIAmParticipantWithoutAccessFilter(currentUser),
            labelKey: 'search.filters.iAmParticipantWithoutAccess',
        },
    ];

    if (features?.[FeatureFlag.PREASSESSED_TEMPLATE]) {
        predefinedFilters.push({
            filters: getTemplatesFilter(currentUser, intl),
            labelKey: 'search.filters.activityTemplates',
        } satisfies NonNullable<SearchPageProps<ActivityGridReadDTO>['predefinedFilters']>[number]);
    }

    return (
        <SearchPageWithPdfPreview
            title={intl.formatMessage({ id: isWatchlist ? 'title.activityWatchlist' : 'title.activitySearch' })}
            filterDialogTitle={intl.formatMessage({ id: isWatchlist ? 'activity.watchlist' : 'activity.name' })}
            columns={getColumns(intl)}
            pageKey={isWatchlist ? 'WatchlistPage' : 'ActivitySearchPage'}
            documentType={DocumentType.ACTIVITY}
            filterSchemas={filterSchemas(intl, features)}
            pagedApiCall={isWatchlist ? getWatchlistActivities : getActivities}
            CardItem={({ item, handleExpandToggle, isExpanded }) => (
                <DocumentResultCard
                    key={item.id}
                    document={{ type: EntityType.ActivityNim, activity: item }}
                    isExpanded={isExpanded(item)}
                    onExpandToggle={() => handleExpandToggle(item)}
                />
            )}
            initialSort={{ id: 'id', desc: true }}
            selectable={false}
            selectedRows={selectedRows}
            onSelect={setSelectedRows}
            getRowId={(row) => row.id}
            savedFilterType={SavedFilterType.ACTIVITY}
            predefinedFilters={predefinedFilters}
            idGetter={(o) => o}
            fetchPdf={getActivityPdf}
            columnHelper={columnHelper}
            bulkActions={{
                options: [
                    ActivityBulkActions.INTERRUPT,
                    ActivityBulkActions.RESUME,
                    ActivityBulkActions.CLOSE,
                    ActivityBulkActions.TAG,
                ],
                translations: ACTIVITY_BULK_ACTIONS,
                page: ROUTES.activity.bulkActions,
            }}
        />
    );
};
